import { CheckCircleIcon, EyeIcon, PencilSquareIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'radash';

import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Tooltip } from '@/components/core/Tooltip';
import { toPixelColumnSize } from '@/services/utils';

import { EnhancedContentDataType } from './enhanced-content.types';

interface ColumnsProps {
    t: (key: string) => string;
    onEdit: (entity: EnhancedContentDataType) => void;
    onDelete: (entities: EnhancedContentDataType[]) => void;
    onToggleApproval: (entity: EnhancedContentDataType) => void;
    canApprove: boolean;
    isReviewer: boolean

}

export const columns: (
    props: ColumnsProps,
) => ColumnDef<EnhancedContentDataType>[] = ({
    t,
    onEdit,
    onDelete,
    onToggleApproval,
    canApprove,
    isReviewer
}) => [
    {
        id: 'select',
        size: toPixelColumnSize(0.05),
        enableResizing: false,
        header: ({ table }) => (
            <Checkbox
                className='tw-mt-[5px]'
                isChecked={table.getIsAllRowsSelected()}
                onCheckedChange={(value) => {
                    table.getToggleAllRowsSelectedHandler()({
                        target: { value },
                    });
                }}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className='tw-mt-[5px]'
                isChecked={row.getIsSelected()}
                onCheckedChange={row.getToggleSelectedHandler()}
            />
        ),
    },
    {
        accessorKey: 'name',
        header: t('ENHANCED_CONTENT.ENTRY_NAME'),
        size: toPixelColumnSize(0.15),
        minSize: 120,
    },
    {
        accessorKey: 'changedAt',
        header: t('ENHANCED_CONTENT.CHANGED_AT'),
        size: toPixelColumnSize(0.125),
        minSize: 120,
        cell: ({ row }) => (
            <div className="tw-whitespace-nowrap">
                {new Date(row.getValue('changedAt')).toLocaleString()}
            </div>
        ),
    },
    {
        accessorKey: 'publishedAt',
        header: t('ENHANCED_CONTENT.PUBLISHED_AT'),
        size: toPixelColumnSize(0.125),
        minSize: 120,
        cell: ({ row }) => (
            <div className="tw-whitespace-nowrap">
                {row.getValue('publishedAt') && !isEmpty(row.original?.retailerLinkIds) && !row.original.needsPublishing && (row.original.approved ?? true)
                    ? new Date(row.getValue('publishedAt')).toLocaleString()
                    : ''}
            </div>
        ),
    },
    {
        accessorKey: 'approved',
        header: t('ENHANCED_CONTENT.ENTRY_STATUS'),
        size: toPixelColumnSize(0.125),
        minSize: 50,
        cell: ({ row }) => {
            const approved = row.original.approved ?? true;
            const approvedAt = row.original.approvedAt ? new Date(row.original.approvedAt).toLocaleString() : '';
            return approved && <Tooltip popupContent={t('ENHANCED_CONTENT.APPROVED_AT')+' '+ approvedAt}>
                <Icon icon={CheckCircleIcon} size={'lg'}/>
            </Tooltip>;
        },
    },
    {
        id: 'actions',
        size: toPixelColumnSize(0.150),
        enableResizing: false,
        cell: ({ row }) => (
            <div className="tw-flex lg:tw-flex-row-reverse tw-flex-col tw-items-center">
                {!isReviewer && <Tooltip popupContent={t('SIMPLE_MAPPING.DELETE')}>
                    <IconButton
                        onClick={() => onDelete([row.original])}
                        icon={TrashIcon}
                        variant="destructive"
                    />
                </Tooltip>}
                <Tooltip popupContent={t('SIMPLE_MAPPING.EDIT')}>
                    <IconButton
                        onClick={() => onEdit(row.original)}
                        icon={!isReviewer ? PencilSquareIcon: EyeIcon}
                        variant="default"
                    />
                </Tooltip>
                <Tooltip
                    popupContent={
                        canApprove
                            ? row.original.approved ?? true
                                ? t('ENHANCED_CONTENT.UNAPPROVE')
                                : t('ENHANCED_CONTENT.APPROVE')
                            : t('USER.ERROR_PERMISSION')
                    }
                >
                    <IconButton
                        disabled={!canApprove}
                        onClick={() => onToggleApproval(row.original)}
                        icon={
                            row.original.approved ?? true
                                ? XCircleIcon
                                : CheckCircleIcon
                        }
                        variant={
                            row.original.approved ?? true
                                ? 'destructive'
                                : 'default'
                        }
                    />
                </Tooltip>
            </div>
        ),
    },
];
